import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react'
import { uploadAlbumPhoto, type PhotoTarget } from '@/lib/albums'
import { limiter } from '@/lib/limit'
import { preparePhoto } from '@/lib/photos'
import { whileWorking } from '@/lib/update'

export type Upload = {
  key: string
  // Whose tray shows it: the album, person or circle it was added from (see ownerOf).
  owner: string
  file: File
  // The photo's own thumbnail once it's made. Not the original: drawing a pile of full-size photos into little
  // squares kept the phone too busy to prepare the uploads themselves.
  preview?: string
  state: 'waiting' | 'adding' | 'added' | 'failed'
  to: PhotoTarget
  id?: string
  error?: string
}
export type Adding = ReturnType<typeof useAddPhotos>

export const ownerOf = (to: PhotoTarget) =>
  'albumId' in to
    ? `album:${to.albumId}`
    : to.people?.length
      ? `person:${to.people.join(',')}`
      : `circle:${to.familyId}`

// Adding photos: as many as you like at once, to an album or to none. Each is prepared in the browser (the original
// kept exactly as it is, with a display copy and thumbnail made beside it) and sent with the date the camera recorded.
// The uploads live here rather than in a page, so leaving the page doesn't stop them or lose track of them: coming
// back shows them still going. Preparing is the phone's work and sending is the connection's, so the next photos are
// made ready while others are on their way.
const preparing = limiter(2)
const sending = limiter(3)
// Few enough at once that a long batch never holds many prepared copies in memory.
const inHand = limiter(5)

let uploads: Upload[] = []
const listeners = new Set<() => void>()
const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
function change(next: Upload[]) {
  uploads = next
  listeners.forEach((listener) => listener())
}
const set = (key: string, patch: Partial<Upload>) =>
  change(uploads.map((x) => (x.key === key ? { ...x, ...patch } : x)))

// Bytes sent so far for each upload still going, kept apart from the list above so the page around the tray doesn't
// redraw on every step of every upload; only the tray's bar listens.
const sent = new Map<string, number>()
const progressListeners = new Set<() => void>()
let progressVersion = 0
let progressTimer: ReturnType<typeof setTimeout> | null = null
function told(key: string, bytes: number) {
  sent.set(key, bytes)
  progressTimer ??= setTimeout(() => {
    progressTimer = null
    progressVersion++
    progressListeners.forEach((listener) => listener())
  }, 200)
}
const subscribeProgress = (listener: () => void) => {
  progressListeners.add(listener)
  return () => progressListeners.delete(listener)
}

// How far along a tray's uploads are, from 0 to 1, by bytes sent: one big photo going slowly shows as such.
export function useUploadProgress(list: Upload[]) {
  const version = useSyncExternalStore(subscribeProgress, () => progressVersion)
  return useMemo(() => {
    void version
    return sentShare(list, sent)
  }, [list, version])
}

export function sentShare(list: Pick<Upload, 'key' | 'file' | 'state'>[], sentSoFar: ReadonlyMap<string, number>) {
  let total = 0
  let done = 0
  for (const x of list) {
    total += x.file.size
    if (x.state === 'added') done += x.file.size
    else if (x.state === 'adding') done += Math.min(x.file.size, sentSoFar.get(x.key) ?? 0)
  }
  return total === 0 ? 0 : done / total
}

async function run(batch: Upload[], refresh: () => Promise<void>, onAdded: (ids: string[]) => void) {
  const ids = new Map<string, string>()
  // New photos show in the page as they arrive, a second or so apart rather than all at the end.
  let soon: ReturnType<typeof setTimeout> | null = null
  const showSoon = () => {
    soon ??= setTimeout(() => {
      soon = null
      void refresh()
    }, 1000)
  }
  await Promise.all(
    batch.map((item) =>
      inHand(async () => {
        set(item.key, { state: 'adding', error: undefined })
        sent.delete(item.key)
        try {
          // No date goes up with it: one is only ever set by someone, and until then it counts the day it was added.
          const photo = await preparing(() => preparePhoto(item.file, { medium: true }))
          if (!uploads.find((x) => x.key === item.key)?.preview)
            set(item.key, { preview: URL.createObjectURL(photo.thumb) })
          // The original is most of what goes up, so its share of the copies' bytes counts as sent with it.
          const id = await sending(() =>
            uploadAlbumPhoto(item.to, photo, (bytes, total) => told(item.key, (bytes / total) * item.file.size)),
          )
          ids.set(item.key, id)
          set(item.key, { state: 'added', id })
          showSoon()
        } catch (e) {
          set(item.key, { state: 'failed', error: e instanceof Error ? e.message : String(e) })
        } finally {
          sent.delete(item.key)
        }
      }),
    ),
  )
  if (soon) clearTimeout(soon)
  await refresh()
  // The new photos, in the order they were chosen, are what the walk-through after works on.
  const added = batch.flatMap((x) => ids.get(x.key) ?? [])
  if (added.length > 0) onAdded(added)
}

function refresher(queryClient: QueryClient, to: PhotoTarget, familyId: string) {
  return async () => {
    if ('albumId' in to) await queryClient.invalidateQueries({ queryKey: ['album', to.albumId] })
    else await queryClient.invalidateQueries({ queryKey: ['found-photos', familyId] })
    await queryClient.invalidateQueries({ queryKey: ['albums', familyId] })
    await queryClient.invalidateQueries({ queryKey: ['stream', familyId] })
  }
}

export function useAddPhotos(to: PhotoTarget, familyId: string, onAdded: (ids: string[]) => void) {
  const queryClient = useQueryClient()
  const owner = ownerOf(to)
  const all = useSyncExternalStore(subscribe, () => uploads)
  const mine = useMemo(() => all.filter((x) => x.owner === owner), [all, owner])
  // What follows a batch (like the album's walk-through) happens only if this page is still open when it's done.
  const latest = useRef(onAdded)
  const open = useRef(true)
  useEffect(() => {
    latest.current = onAdded
  })
  useEffect(() => {
    open.current = true
    return () => {
      open.current = false
    }
  }, [])

  const start = useCallback(
    (batch: Upload[], where: PhotoTarget) => {
      const after = (ids: string[]) => open.current && latest.current(ids)
      void whileWorking(run(batch, refresher(queryClient, where, familyId), after))
    },
    [queryClient, familyId],
  )

  // Each batch can go somewhere of its own (photos in no album, shared with a different space), else to `to`.
  function add(files: File[], where: PhotoTarget = to) {
    const batch = files.map((file) => ({
      key: crypto.randomUUID(),
      owner,
      to: where,
      file,
      state: 'waiting' as const,
    }))
    // This tray's photos that are already in make room for the new ones.
    const gone = uploads.filter((x) => x.owner === owner && x.state === 'added')
    gone.forEach((x) => x.preview && URL.revokeObjectURL(x.preview))
    change([...uploads.filter((x) => !gone.includes(x)), ...batch])
    start(batch, where)
  }

  const retry = () => {
    const failed = mine.filter((x) => x.state === 'failed')
    if (failed.length === 0) return
    const where = failed[0]!.to
    start(failed, where)
  }
  // Photos still on their way or that failed; ones already in have shown up in the page itself.
  const pending = mine.filter((x) => x.state !== 'added').length
  return { uploads: mine, pending, add, retry }
}
